import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import AppIcon from '../../../components/Icon';
import { useAccordion } from './Accordion';

type Props = {
    id: string;
    title: string;
    subtitle?: string;
    defaultOpen?: boolean;
    alwaysOpen?: boolean;
    children: React.ReactNode;
};

export default function Section({ id, title, subtitle, defaultOpen = true, alwaysOpen = false, children }: Props) {
    const ctx = useAccordion();
    const [localOpen, setLocalOpen] = useState(defaultOpen);
    const open = alwaysOpen ? true : (ctx ? ctx.openId === id : localOpen);
    const contentRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<string>(open ? '999px' : '0px');

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const target = open ? `${el.scrollHeight}px` : '0px';
        // Trigger transition by setting maxHeight after a tick
        requestAnimationFrame(() => setMaxHeight(target));
    }, [open, children]);
    return (
        <div className="mb-4">
            <button
                onClick={() => {
                    if (alwaysOpen) return;
                    if (ctx) ctx.setOpenId(open ? null : id);
                    else setLocalOpen((v) => !v);
                }}
                className="w-full cursor-default select-none text-left"
            >
                <div className="flex items-center justify-between px-4">
                    <div className="text-[11px] font-semibold tracking-wider text-indigo-500 dark:text-indigo-400">
                        {title.toUpperCase()}
                    </div>
                    {!alwaysOpen && (
                        <AppIcon icon={FiChevronDown} className={[
                            'transition-transform text-gray-500 dark:text-gray-400',
                            open ? 'rotate-180' : 'rotate-0'
                        ].join(' ')} />
                    )}
                </div>
                {subtitle && (
                    <div className="px-4 pb-2 text-[11px] text-gray-500 dark:text-gray-400">{subtitle}</div>
                )}
            </button>
            <div
                ref={contentRef}
                className="mt-1 overflow-hidden border-t border-gray-200 dark:border-gray-800/40 transition-[max-height] duration-300 ease-in-out"
                style={{ maxHeight: alwaysOpen ? 'none' : maxHeight }}
                aria-hidden={!open}
            >
                <div className="space-y-1 pt-2">
                    {children}
                </div>
            </div>
        </div>
    );
}


